import { Plus } from 'lucide-react';
import Toggle from '../components/Toggle';
import { ic, lc, formatDOBInput } from '../helpers';
import { PREFIXES, SUFFIXES, STATES, GENDERS } from '../constants';

// ─────────────────────────────────────────
// STEP 2 — PERSONAL INFO + SPOUSE + DRIVERS
// ─────────────────────────────────────────
export default function Step2Personal({
  personal,
  setPersonal,
  spouse,
  setSpouse,
  drivers,
  setDrivers,
}) {
  const hasSpouse = ['Married', 'Domestic Partner'].includes(personal.maritalStatus);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
        <h2 className="text-base font-semibold text-slate-800 pb-1 border-b">Personal Information</h2>

        <div className="space-y-3">
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-3">
              <label className={lc}>Prefix</label>
              <select value={personal.prefix} onChange={e => setPersonal(p => ({ ...p, prefix: e.target.value }))} className={ic}>
                <option value="">—</option>
                {PREFIXES.map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div className="col-span-6">
              <label className={lc}>First Name</label>
              <input value={personal.firstName} onChange={e => setPersonal(p => ({ ...p, firstName: e.target.value }))} placeholder="First name" className={ic} />
            </div>
            <div className="col-span-3">
              <label className={lc}>MI</label>
              <input maxLength={1} value={personal.middleInitial} onChange={e => setPersonal(p => ({ ...p, middleInitial: e.target.value }))} placeholder="M" className={ic} />
            </div>
          </div>
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-9">
              <label className={lc}>Last Name</label>
              <input value={personal.lastName} onChange={e => setPersonal(p => ({ ...p, lastName: e.target.value }))} placeholder="Last name" className={ic} />
            </div>
            <div className="col-span-3">
              <label className={lc}>Suffix</label>
              <select value={personal.suffix} onChange={e => setPersonal(p => ({ ...p, suffix: e.target.value }))} className={ic}>
                <option value="">—</option>
                {SUFFIXES.map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={lc}>Date of Birth</label>
            <input
              type="text"
              value={personal.dateOfBirth}
              onChange={e => setPersonal(p => ({ ...p, dateOfBirth: formatDOBInput(e.target.value) }))}
              placeholder="MM/DD/YYYY"
              className={ic}
            />
          </div>
          <div>
            <label className={lc}>Gender</label>
            <select value={personal.gender} onChange={e => setPersonal(p => ({ ...p, gender: e.target.value }))} className={ic}>
              <option value="">Select...</option>
              {GENDERS.map(g => <option key={g}>{g}</option>)}
            </select>
          </div>
          <div>
            <label className={lc}>Marital Status</label>
            <select value={personal.maritalStatus} onChange={e => setPersonal(p => ({ ...p, maritalStatus: e.target.value }))} className={ic}>
              <option value="">Select...</option>
              {['Single','Married','Divorced','Widowed','Domestic Partner'].map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className={lc}>Phone Number</label>
            <input type="tel" value={personal.phone} onChange={e => setPersonal(p => ({ ...p, phone: e.target.value }))} placeholder="(555) 000-0000" className={ic} />
          </div>
          <div>
            <label className={lc}>Driver's License #</label>
            <input value={personal.driversLicenseNumber} onChange={e => setPersonal(p => ({ ...p, driversLicenseNumber: e.target.value }))} placeholder="License number" className={ic} />
          </div>
          <div>
            <label className={lc}>License State</label>
            <select value={personal.driversLicenseState} onChange={e => setPersonal(p => ({ ...p, driversLicenseState: e.target.value }))} className={ic}>
              <option value="">Select...</option>
              {STATES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className={lc}>Employer</label>
            <input value={personal.employer} onChange={e => setPersonal(p => ({ ...p, employer: e.target.value }))} placeholder="Company name" className={ic} />
          </div>
          <div>
            <label className={lc}>Occupation</label>
            <input value={personal.occupation} onChange={e => setPersonal(p => ({ ...p, occupation: e.target.value }))} placeholder="Job title" className={ic} />
          </div>
        </div>

        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200">
            <p className="text-sm font-semibold text-slate-700">Driver Questions</p>
          </div>
          <div className="px-4 divide-y divide-slate-100">
            <Toggle
              label="Does this driver spend 3 or more months out of state each year?"
              checked={personal.spendMonthsOutOfState}
              onChange={v => setPersonal(p => ({ ...p, spendMonthsOutOfState: v, takesAutosOutOfState: v ? p.takesAutosOutOfState : false }))}
            />
            {personal.spendMonthsOutOfState && (
              <Toggle
                label="Does this driver take any autos listed on the policy with them?"
                checked={personal.takesAutosOutOfState}
                onChange={v => setPersonal(p => ({ ...p, takesAutosOutOfState: v }))}
              />
            )}
            <Toggle
              label="Have you been licensed in another state in the past 2 years?"
              checked={personal.licensedAnotherState}
              onChange={v => setPersonal(p => ({
                ...p,
                licensedAnotherState: v,
                priorLicenseState: v ? p.priorLicenseState : '',
                priorLicenseNumber: v ? p.priorLicenseNumber : '',
              }))}
            />
            {personal.licensedAnotherState && (
              <div className="py-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={lc}>Prior License State</label>
                  <select value={personal.priorLicenseState} onChange={e => setPersonal(p => ({ ...p, priorLicenseState: e.target.value }))} className={ic}>
                    <option value="">Select...</option>
                    {STATES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className={lc}>Prior License Number</label>
                  <input value={personal.priorLicenseNumber} onChange={e => setPersonal(p => ({ ...p, priorLicenseNumber: e.target.value }))} placeholder="License number" className={ic} />
                </div>
              </div>
            )}
          </div>
        </div>

        {hasSpouse && (
          <div className="border border-blue-200 rounded-xl overflow-hidden">
            <div className="bg-blue-50 px-4 py-2.5 border-b border-blue-200">
              <p className="text-sm font-semibold text-blue-800">
                {personal.maritalStatus === 'Domestic Partner' ? 'Domestic Partner Info' : 'Spouse Info'}
              </p>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-12 gap-3">
                <div className="col-span-3">
                  <label className={lc}>Prefix</label>
                  <select value={spouse.prefix} onChange={e => setSpouse(s => ({ ...s, prefix: e.target.value }))} className={ic}>
                    <option value="">—</option>
                    {PREFIXES.map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div className="col-span-6">
                  <label className={lc}>First Name</label>
                  <input value={spouse.firstName} onChange={e => setSpouse(s => ({ ...s, firstName: e.target.value }))} placeholder="First name" className={ic} />
                </div>
                <div className="col-span-3">
                  <label className={lc}>MI</label>
                  <input maxLength={1} value={spouse.middleInitial} onChange={e => setSpouse(s => ({ ...s, middleInitial: e.target.value }))} placeholder="M" className={ic} />
                </div>
              </div>
              <div className="grid grid-cols-12 gap-3">
                <div className="col-span-9">
                  <label className={lc}>Last Name</label>
                  <input value={spouse.lastName} onChange={e => setSpouse(s => ({ ...s, lastName: e.target.value }))} placeholder="Last name" className={ic} />
                </div>
                <div className="col-span-3">
                  <label className={lc}>Suffix</label>
                  <select value={spouse.suffix} onChange={e => setSpouse(s => ({ ...s, suffix: e.target.value }))} className={ic}>
                    <option value="">—</option>
                    {SUFFIXES.map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className={lc}>Date of Birth</label>
                <input
                  type="text"
                  value={spouse.dateOfBirth}
                  onChange={e => setSpouse(s => ({ ...s, dateOfBirth: formatDOBInput(e.target.value) }))}
                  placeholder="MM/DD/YYYY"
                  className={ic}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="border border-slate-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between bg-slate-50 px-4 py-2.5 border-b border-slate-200">
          <div>
            <p className="text-sm font-semibold text-slate-700">Additional Household Drivers</p>
            <p className="text-xs text-slate-500 mt-0.5">Anyone else in your household who may drive a vehicle on this policy</p>
          </div>
          <button
            onClick={() => setDrivers(prev => [...prev, { firstName: '', lastName: '', dateOfBirth: '', gender: '', relationshipToInsured: '' }])}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 transition"
          >
            <Plus size={13} /> Add Driver
          </button>
        </div>
        {drivers.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4 px-4">No additional drivers. Click "Add Driver" to add a household member.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {drivers.map((driver, i) => (
              <div key={i} className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-slate-700">Driver {i + 1}</span>
                  <button
                    onClick={() => setDrivers(prev => prev.filter((_, idx) => idx !== i))}
                    className="text-xs text-red-500 hover:text-red-700"
                  >
                    Remove
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={lc}>First Name</label>
                    <input
                      value={driver.firstName}
                      onChange={e => setDrivers(prev => prev.map((d, idx) => idx === i ? { ...d, firstName: e.target.value } : d))}
                      placeholder="First name"
                      className={ic}
                    />
                  </div>
                  <div>
                    <label className={lc}>Last Name</label>
                    <input
                      value={driver.lastName}
                      onChange={e => setDrivers(prev => prev.map((d, idx) => idx === i ? { ...d, lastName: e.target.value } : d))}
                      placeholder="Last name"
                      className={ic}
                    />
                  </div>
                  <div>
                    <label className={lc}>Date of Birth</label>
                    <input
                      type="text"
                      value={driver.dateOfBirth}
                      onChange={e => setDrivers(prev => prev.map((d, idx) => idx === i ? { ...d, dateOfBirth: formatDOBInput(e.target.value) } : d))}
                      placeholder="MM/DD/YYYY"
                      className={ic}
                    />
                  </div>
                  <div>
                    <label className={lc}>Gender</label>
                    <select
                      value={driver.gender}
                      onChange={e => setDrivers(prev => prev.map((d, idx) => idx === i ? { ...d, gender: e.target.value } : d))}
                      className={ic}
                    >
                      <option value="">Select...</option>
                      {['Male','Female','Non-binary','Prefer not to say'].map(g => <option key={g}>{g}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className={lc}>Relationship to Insured</label>
                    <select
                      value={driver.relationshipToInsured}
                      onChange={e => setDrivers(prev => prev.map((d, idx) => idx === i ? { ...d, relationshipToInsured: e.target.value } : d))}
                      className={ic}
                    >
                      <option value="">Select...</option>
                      {['Spouse','Child','Parent','Sibling','Other Relative','Other'].map(r => <option key={r}>{r}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}